import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";
import { IconButton, CircularProgress, Menu, MenuItem } from "@material-ui/core";
import { uniqBy } from "lodash";
// STYLES
import { DashboardWrapper, HideBrowserScrollbars } from "./CommonDashboard.style";
// HELPERS
import { Api, IMAGES, prefetchExecutiveOvalImage } from "Helpers";
import { resolveProfilePic, defaultProfile } from "Helpers/resolveProfilePic";
import {
	URL_CIVICS_DASHBOARD,
	URL_HISTORY_DASHBOARD,
	URL_FOREIGN_LANGUAGE_DASHBOARD,
	URL_SCIENCE_DASHBOARD,
	URL_ECONOMIC_DASHBOARD,
	URL_ENGLISH_DASHBOARD,
	URL_MATHS_DASHBOARD,
	URL_PROFILE,
	URL_CHANGE_PWD,
	URL_ABOUT_US,
	URL_TERMS_AND_CONDITIONS,
	URL_PRIVACY_POLICY,
	URL_CONTACT_US,
	URL_DATA_SECURITY,
} from "Helpers/Paths";
// REDUX
import { showToast } from "Redux/General/Actions";
import { logOutUser, subjectAccessData, selectedSubjectValue } from "Redux/Auth/Actions";
// COMPONENTS
import { AnimatedButton } from "Components/Commons";
import LazyImage from "Components/Commons/LazyImage/LazyImage";
// ASSETS
import UserLoginSound from "Assets/Sounds/Dashboard/UserLoginMusic.mp3";
// Student dashboard subject artwork (card, background, compressed background, side illustration)
import CivicsCard from "Assets/Images/StudentDashboard/civics-card.webp";
import CivicsBg from "Assets/Images/StudentDashboard/civics-bg.webp";
import CivicsBgSmall from "Assets/Images/StudentDashboard/civics-bg-small.webp";
import CivicsIcon from "Assets/Images/StudentDashboard/civics-icon.webp";
import HistoryCard from "Assets/Images/StudentDashboard/history-card.webp";
import HistoryBg from "Assets/Images/StudentDashboard/history-bg.webp";
import HistoryBgSmall from "Assets/Images/StudentDashboard/history-bg-small.webp";
import HistoryIcon from "Assets/Images/StudentDashboard/history-icon.webp";
import EnglishCard from "Assets/Images/StudentDashboard/english-card.webp";
import EnglishBg from "Assets/Images/StudentDashboard/english-bg.webp";
import EnglishBgSmall from "Assets/Images/StudentDashboard/english-bg-small.webp";
import EnglishIcon from "Assets/Images/StudentDashboard/english-icon.webp";
import ScienceCard from "Assets/Images/StudentDashboard/science-card.webp";
import ScienceBg from "Assets/Images/StudentDashboard/science-bg.webp";
import ScienceBgSmall from "Assets/Images/StudentDashboard/science-bg-small.webp";
import ScienceIcon from "Assets/Images/StudentDashboard/science-icon.webp";
import MathsCard from "Assets/Images/StudentDashboard/maths-card.webp";
import MathsBg from "Assets/Images/StudentDashboard/maths-bg.webp";
import MathsBgSmall from "Assets/Images/StudentDashboard/maths-bg-small.webp";
import MathsIcon from "Assets/Images/StudentDashboard/maths-icon.webp";
import EconomicsCard from "Assets/Images/StudentDashboard/economics-card.webp";
import EconomicsBg from "Assets/Images/StudentDashboard/economics-bg.webp";
import EconomicsBgSmall from "Assets/Images/StudentDashboard/economics-bg-small.webp";
import EconomicsIcon from "Assets/Images/StudentDashboard/economics-icon.webp";
import ForeignLanguageCard from "Assets/Images/StudentDashboard/foreign-language-card.webp";
import ForeignLanguageBg from "Assets/Images/StudentDashboard/foreign-language-bg.webp";
import ForeignLanguageBgSmall from "Assets/Images/StudentDashboard/foreign-language-bg-small.webp";
import ForeignLanguageIcon from "Assets/Images/StudentDashboard/foreign-language-icon.webp";

const subjectData = [
	{
		id: 1,
		image: CivicsCard,
		name: "civics",
		label: "Civics",
		accent: "#5B9BFF",
		accentRgb: "91, 155, 255",
		quote: "Education is the key to unlocking your Future. Understanding your Government is the key to unlocking the World.",
		quoteBy: "",
		navigateTo: URL_CIVICS_DASHBOARD,
		backgroundImg: CivicsBg,
		sideLogo: CivicsIcon,
		tinyImage: CivicsBgSmall,
	},
	{
		id: 2,
		image: HistoryCard,
		name: "history",
		label: "History",
		accent: "#E8B864",
		accentRgb: "232, 184, 100",
		quote: "\"A people without the knowledge of their past history, origin and culture is like a tree without roots.\"",
		quoteBy: "",
		navigateTo: URL_HISTORY_DASHBOARD,
		backgroundImg: HistoryBg,
		sideLogo: HistoryIcon,
		tinyImage: HistoryBgSmall,
	},
	{
		id: 3,
		image: EnglishCard,
		name: "english",
		label: "English",
		accent: "#FFB547",
		accentRgb: "255, 181, 71",
		quote: "\"What is the longest word in the English language? SMILES: there is a mile between the first and last letters!\"",
		quoteBy: "",
		navigateTo: URL_ENGLISH_DASHBOARD,
		backgroundImg: EnglishBg,
		sideLogo: EnglishIcon,
		tinyImage: EnglishBgSmall,
	},
	{
		id: 4,
		image: ScienceCard,
		name: "science",
		label: "Science",
		accent: "#3FD8F0",
		accentRgb: "63, 216, 240",
		quote: "\"No amount of experimentation can ever prove me right; a single experiment can prove me wrong.\"",
		quoteBy: "By: Albert Einstein",
		navigateTo: URL_SCIENCE_DASHBOARD,
		backgroundImg: ScienceBg,
		sideLogo: ScienceIcon,
		tinyImage: ScienceBgSmall,
	},
	{
		id: 5,
		image: MathsCard,
		name: "math",
		label: "Maths",
		accent: "#FF9A4D",
		accentRgb: "255, 154, 77",
		quote: "\"Life is a Math Education. In order to gain the most, you have to know how to convert negatives into POSITIVES.\"",
		quoteBy: "",
		navigateTo: URL_MATHS_DASHBOARD,
		backgroundImg: MathsBg,
		sideLogo: MathsIcon,
		tinyImage: MathsBgSmall,
	},
	{
		id: 6,
		image: EconomicsCard,
		name: "economics",
		label: "Economics",
		accent: "#3FDC9A",
		accentRgb: "63, 220, 154",
		quote: "\"The ultimate resource in economic development is people. It is people, not capital or raw materials that develop an economy.\"",
		quoteBy: "",
		backgroundImg: EconomicsBg,
		sideLogo: EconomicsIcon,
		tinyImage: EconomicsBgSmall,
		navigateTo: URL_ECONOMIC_DASHBOARD,
	},
	{
		id: 7,
		image: ForeignLanguageCard,
		name: "foreign language",
		label: "Foreign Language",
		accent: "#4FE0C8",
		accentRgb: "79, 224, 200",
		quote: "\"One language sets you in a corridor for life. Two languages open every door along the way.\"",
		quoteBy: "By: Frank Smith",
		backgroundImg: ForeignLanguageBg,
		navigateTo: URL_FOREIGN_LANGUAGE_DASHBOARD,
		sideLogo: ForeignLanguageIcon,
		tinyImage: ForeignLanguageBgSmall,
	},
];

// The last selected subject is kept in localStorage together with its image URLs,
// so re-read the artwork from subjectData by id; otherwise returning students keep seeing the old images.
const withCurrentArtwork = (subject) => (subject && subject.id ? subjectData.find((item) => item.id === subject.id) || subject : {});

const menuItem = ["My Profile", "Change Password", "About us", "Privacy policy", "Terms & Conditions", "Data Security", "Contact Us", "Logout"];

function CommonDashboard() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const profileInfo = useSelector((state) => state.Auth.profileInfo);
	let subjectStatus = useSelector((state) => state.Auth.subjectAccessList);
	let subjectSelected = useSelector((state) => state.Auth.selectedSubjectDataValue);

	const [subLoading, setSubLoading] = useState(false);
	const [subjectAccess, setsubjectAccess] = useState([]);
	const [openProfileMenu, setOpenProfileMenu] = useState(false);
	const [subjectsToShow, setSubjectsToShow] = useState(subjectData);
	const [selectedSubjectData, setSelectedSubjectData] = useState(withCurrentArtwork(subjectSelected));
	const menuList = profileInfo?.isFromClassLink ? menuItem.filter((menu) => menu !== "Change Password") : menuItem;

	useEffect(() => {
		getSubjectAccess();
	}, []);

	useEffect(() => {
		if (selectedSubjectData?.name === "civics") prefetchExecutiveOvalImage();
	}, [selectedSubjectData?.name]);

	//SUBJECT ACCESS FOR STUDENT
	const getSubjectAccess = async () => {
		setSubLoading(true);

		const newSubjectsToShow = [...subjectsToShow];

		let response = await new Api().get("general/getSubjectAccess");

		if (response.status === 200) subjectStatus = response?.data?.subjectAccessData;

		let subjectAccess = [];

		subjectStatus &&
			subjectStatus.forEach((data) => {
				if (!subjectAccess.includes(data.subject)) subjectAccess.push(data.subject.toLowerCase());
			});

		let subject = { data: subjectStatus && subjectStatus.length ? subjectStatus : [] };

		dispatch(subjectAccessData(subject));

		let showSubject = newSubjectsToShow.filter(function (value) {
			return subjectAccess.includes(value.name);
		});

		showSubject = uniqBy(showSubject, "id");

		setSubjectsToShow(showSubject);
		setsubjectAccess(subjectAccess);
		setSubLoading(false);
	};

	function logout() {
		let data = new FormData();

		data.append("token", localStorage.getItem("accessToken"));

		new Api().post("user/logout", { data });

		dispatch(logOutUser());
	}

	const arrowSettings = { nextArrow: <SlideNextArrow />, prevArrow: <SlidePreviousArrow /> };
	const slidesFor = (count) => ({ slidesToShow: count, slidesToScroll: 1, infinite: subjectsToShow.length > count, dots: false, ...arrowSettings });

	const settings = {
		dots: false,
		infinite: false,
		slidesToShow: 7,
		slidesToScroll: 1,
		initialSlide: 0,
		...arrowSettings,
		responsive: [
			{ breakpoint: 1599, settings: slidesFor(6) },
			{ breakpoint: 1379, settings: slidesFor(5) },
			{ breakpoint: 1171, settings: slidesFor(4) },
			{ breakpoint: 964, settings: slidesFor(3) },
			{ breakpoint: 758, settings: slidesFor(2) },
		],
	};


	const handlesubjectAccess = (data) => {
		setSelectedSubjectData(data);
		dispatch(selectedSubjectValue(data));
		// Start caching the Executive panorama while the student is still on this screen
		if (data?.name === "civics") prefetchExecutiveOvalImage();
	};

	const handleSubject = () => {
		if (selectedSubjectData) {
			if (selectedSubjectData.name === "civics") prefetchExecutiveOvalImage();
			navigate(selectedSubjectData.navigateTo);
		}
	};

	const handleOpenMenu = (event) => setOpenProfileMenu(event.currentTarget);

	const handleCloseMenu = (data) => {
		setOpenProfileMenu(false);

		let dataUrl = "";

		switch (data) {
			case "My Profile":
				dataUrl = "/" + URL_PROFILE;
				break;

			case "Change Password":
				dataUrl = "/" + URL_CHANGE_PWD;
				break;

			case "About us":
				dataUrl = "/" + URL_ABOUT_US;
				break;

			case "Privacy policy":
				dataUrl = "/" + URL_PRIVACY_POLICY;
				break;

			case "Terms & Conditions":
				dataUrl = "/" + URL_TERMS_AND_CONDITIONS;
				break;

			case "Data Security":
				dataUrl = "/" + URL_DATA_SECURITY;
				break;

			case "Contact Us":
				dataUrl = URL_CONTACT_US;
				break;

			case "Logout":
				logout();
				break;

			default:
				break;
		}

		if (data !== "Logout" && menuList.includes(data)) navigate(dataUrl);
	};

	return (
		<DashboardWrapper bgImg={selectedSubjectData} profileInfo={profileInfo}>
			<HideBrowserScrollbars />
			<LazyImage
				compressedImage={selectedSubjectData.tinyImage ? selectedSubjectData.tinyImage : IMAGES.LANDING_PAGE.TEACHER_BACKGROUND}
				image={selectedSubjectData.backgroundImg ? selectedSubjectData.backgroundImg : IMAGES.LANDING_PAGE.TEACHER_BACKGROUND}
				wrapperProps={{ className: "background-image" }}
				imageProps={{ style: { objectFit: "cover" } }}
			/>
			<div className="audio">
				<audio loop={true} src={UserLoginSound} autoPlay controls />
			</div>

			<div className="header-container">
				<div className="inner-header">
					<div className="logo-img"></div>
					<div className="profile-container">
						<div className="text">{profileInfo?.firstName + " " + profileInfo?.lastName}</div>
						<img
							className="profile-picture"
							src={resolveProfilePic(profileInfo?.profilePicUrl)}
							alt=""
							onError={(event) => {
								event.currentTarget.onerror = null;
								event.currentTarget.src = defaultProfile;
							}}
						/>
						<div className="arrow" onClick={handleOpenMenu}></div>
						<Menu
							id="simple-menu"
							anchorEl={openProfileMenu}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: -15, horizontal: 140 }}
							open={openProfileMenu}
							onClose={handleCloseMenu}
							getContentAnchorEl={null}
						>
							{menuList.map((item) => (
								<MenuItem key={item} onClick={() => handleCloseMenu(item)}>
									<div textAlign="center">{item}</div>
								</MenuItem>
							))}
						</Menu>
					</div>
				</div>
			</div>

			{subLoading ? (
				<div className="progress">
					<CircularProgress />
				</div>
			) : (
				<>
					{Object.keys(selectedSubjectData).length ? (
						<main className="hero">
							<div className="hero-text">
								<div className="eyebrow">Selected subject</div>
								<h1 className="subject-name">{selectedSubjectData.label || selectedSubjectData.name}</h1>
								{selectedSubjectData.quote && <p className="quote">{selectedSubjectData.quote}</p>}
								{selectedSubjectData.quoteBy && <div className="quote-by">{selectedSubjectData.quoteBy}</div>}
								<AnimatedButton
									className="play"
									onClick={() =>
										subjectAccess.length && subjectAccess.includes(selectedSubjectData?.name)
											? handleSubject()
											: dispatch(showToast(`Sorry!, you don't have access to ${selectedSubjectData?.name}.`))
									}
								>
									Play now
								</AnimatedButton>
							</div>

							{selectedSubjectData?.sideLogo && (
								<div className="hero-art">
									<div key={selectedSubjectData.id} className="hero-art-inner">
										{/* blurred copy behind the illustration: a glow in the icon's own colours */}
										<img className="icon-glow" src={selectedSubjectData.sideLogo} alt="" aria-hidden="true" />
										<img className="icon" src={selectedSubjectData.sideLogo} alt="" />
									</div>
								</div>
							)}
						</main>
					) : (
						<div className="welcome-container flex f-v-center f-h-center">
							<div className="welcome-board-text">Transforming Study time to Game time</div>
						</div>
					)}

					<section className="subject-container" aria-label="Subjects">
						<div className="subject-shelf">
							<Slider {...settings}>
								{subjectsToShow.map((data) => (
									<div key={data.id}>
										<button
											type="button"
											aria-label={data.label || data.name}
											onClick={() => handlesubjectAccess(data)}
											style={{
												backgroundImage: `url(${data.image})`,
												"--accent": data.accent,
												"--accent-rgb": data.accentRgb,
											}}
											className={`subject-img ${data.id === selectedSubjectData?.id ? "selected-subject" : ""}`}
										></button>
									</div>
								))}
							</Slider>
						</div>
					</section>
				</>
			)}
		</DashboardWrapper>
	);
}

const SlideNextArrow = ({ currentSlide, slideCount, ...props }) => (
	<IconButton {...props} className="next-arrow" aria-label="Next subjects">
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M9 18l6-6-6-6" />
		</svg>
	</IconButton>
);

const SlidePreviousArrow = ({ currentSlide, slideCount, ...props }) => (
	<IconButton {...props} className="prev-arrow" aria-label="Previous subjects">
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M15 18l-6-6 6-6" />
		</svg>
	</IconButton>
);

export default CommonDashboard;
